import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles, Globe, Users, Lock, Share2, X, Trash2, ImagePlus } from 'lucide-react';
import { outreachApi } from '../../api/client';
import { nativeShare } from '../../utils/share';
import { useTranslation } from '../../i18n';
import RichEditor from '../courses/components/RichEditor';
import { uploadStoryImage } from './storyMedia';
import { STORY_TEMPLATES, TEMPLATE_BY_ID, promptCopyKey } from './templates';
import './MyStory.css';

const VIS_OPTIONS = [
  { id: 'private',     Icon: Lock },
  { id: 'connections', Icon: Users },
  { id: 'public',      Icon: Globe },
];

// Themes for the Movement feed (matches backend categories + movement.* labels).
const MOVEMENT_CATEGORIES = ['salvation', 'healing', 'provision', 'persecution', 'revival', 'other'];

// Slide-up modal for writing a story. Used for both creating (no storyId) and
// editing (storyId given). Create walks the full flow: pick a starting point →
// answer a few short, skippable prompts → AI weaves a first draft you edit →
// choose who can see it. Edit jumps straight to the draft editor with a way back
// through the questions. The story is created when prompts begin, so leaving
// mid-way keeps a resumable draft. All hooks run before any conditional render
// (Rules of Hooks).
export default function StoryWizard({ storyId: editId = null, onClose, onSaved, onDeleted }) {
  const { t } = useTranslation('testimony');
  const editMode = !!editId;

  const [loading, setLoading] = useState(editMode);
  const [loadFailed, setLoadFailed] = useState(false);
  const [step, setStep] = useState(1);
  const [templateId, setTemplateId] = useState(null);
  const [title, setTitle] = useState('');
  const [answers, setAnswers] = useState({});
  const [promptIdx, setPromptIdx] = useState(0);
  const [storyId, setStoryId] = useState(editId);
  const [html, setHtml] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [shareUrl, setShareUrl] = useState('');

  // Cover image (optional): upload one or generate with AI. Shows on story cards,
  // the profile, and — once public — the Movement feed.
  const [coverImage, setCoverImage] = useState('');
  const [coverPreview, setCoverPreview] = useState(''); // local blob for instant preview after upload
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverGenerating, setCoverGenerating] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // Movement-feed fields, collected when the story is made public. Public stories
  // are auto-submitted to the global Movement feed (pending admin review).
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitterName, setSubmitterName] = useState('');
  const [movement, setMovement] = useState(null); // linked Movement status, if any

  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState('');

  // Edit mode: load the story and open at the draft editor (step 3).
  useEffect(() => {
    if (!editMode) return;
    let alive = true;
    outreachApi.getStory(editId)
      .then(d => {
        if (!alive) return;
        const s = d.story;
        setTitle(s.title || '');
        setTemplateId(s.template || 'freeform');
        setAnswers(s.promptAnswers || {});
        setHtml(s.combinedHtml || '');
        setCoverImage(s.coverImage || '');
        setVisibility(s.visibility || 'private');
        if (s.shareToken && s.isPublic) setShareUrl(`${window.location.origin}/t/${s.shareToken}`);
        if (s.movement) {
          setMovement(s.movement);
          setLocation(s.movement.location || '');
          setCategory(s.movement.category || '');
          setIsAnonymous(!!s.movement.isAnonymous);
          setSubmitterName(s.movement.submitterName || '');
        }
        setStep(3);
      })
      .catch(() => { if (alive) setLoadFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [editMode, editId]);

  // Stop any in-flight cover-image poll when the wizard unmounts.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const tpl = templateId ? TEMPLATE_BY_ID[templateId] : null;
  const prompts = tpl ? tpl.prompts : [];
  const stepLabelsRaw = t('stepLabels');
  const stepLabels = Array.isArray(stepLabelsRaw) ? stepLabelsRaw : ['Start', 'Notes', 'Story', 'Share'];

  function flash(msg) { setToast(msg); setTimeout(() => setToast(''), 2800); }

  function buildAnswers() {
    return prompts
      .filter(pid => (answers[pid] || '').trim())
      .map(pid => ({ q: t(`${promptCopyKey(tpl.id, pid)}.label`), a: answers[pid].trim() }));
  }

  async function startPrompts() {
    if (!templateId || busy) return;
    setBusy(true);
    try {
      if (!storyId) {
        const d = await outreachApi.createStory({ title, template: templateId, color: tpl.color });
        setStoryId(d.story.id);
      } else {
        await outreachApi.updateStory(storyId, { title, template: templateId });
      }
      setPromptIdx(0);
      setStep(2);
    } catch (e) { flash(e.message || t('saveError')); }
    setBusy(false);
  }

  // Move from the questions to the draft. Auto-generate the first draft (when none
  // exists yet); otherwise only rewrite when the user explicitly asks, so a
  // hand-edited story isn't lost just for revisiting an answer.
  async function finishPrompts(regenerate) {
    setBusy(true);
    try { await outreachApi.updateStory(storyId, { promptAnswers: answers }); } catch {}
    setBusy(false);
    setStep(3);
    if (!html || regenerate) generate();
  }

  async function generate() {
    if (generating) return;
    const payload = buildAnswers();
    if (!payload.length) { flash(t('needNotes')); return; }
    setGenerating(true);
    try {
      if (editMode) { try { await outreachApi.updateStory(storyId, { promptAnswers: answers }); } catch {} }
      const d = await outreachApi.generateStory(storyId, { answers: payload });
      setHtml(d.combinedHtml || '');
      if (d.fallback) flash(t('fallbackNote'));
    } catch (e) { flash(e.message || t('saveError')); }
    setGenerating(false);
  }

  async function draftNext() {
    setBusy(true);
    try { await outreachApi.updateStory(storyId, { title, promptAnswers: answers, combinedHtml: html, coverImage }); } catch {}
    setBusy(false);
    setStep(4);
  }

  // ── Cover image ───────────────────────────────────────────────────────────────
  async function pickCover(e) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || coverBusy) return;
    setCoverBusy(true);
    try {
      const { fileUrl, previewUrl } = await uploadStoryImage(file);
      setCoverImage(fileUrl);
      setCoverPreview(previewUrl); // unsigned fileUrl 403s until reload; show the blob now
      try { await outreachApi.updateStory(storyId, { coverImage: fileUrl }); } catch {}
    } catch { flash(t('coverFailed')); }
    setCoverBusy(false);
  }

  async function generateCover() {
    if (coverGenerating || !html) return;
    setCoverGenerating(true);
    try {
      await outreachApi.generateCoverImage(storyId);
      // Poll until the background job saves a coverImage (or we give up ~2 min).
      let tries = 0;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const d = await outreachApi.getStory(storyId);
          if (d.story?.coverImage) {
            setCoverImage(d.story.coverImage);
            setCoverPreview('');
            clearInterval(pollRef.current); pollRef.current = null;
            setCoverGenerating(false);
          }
        } catch {}
        if (tries >= 40 && pollRef.current) {
          clearInterval(pollRef.current); pollRef.current = null;
          setCoverGenerating(false);
          flash(t('coverFailed'));
        }
      }, 3000);
    } catch (e) { setCoverGenerating(false); flash(e.message || t('coverFailed')); }
  }

  async function removeCover() {
    setCoverImage('');
    setCoverPreview('');
    try { await outreachApi.updateStory(storyId, { coverImage: null }); } catch {}
  }

  function movementFields() {
    return { location, category, isAnonymous, submitterName };
  }

  async function handleShare() {
    if (busy) return;
    setBusy(true);
    try {
      await outreachApi.updateStory(storyId, { combinedHtml: html, visibility: 'public', ...movementFields() });
      const { url, movement: mv } = await outreachApi.shareStory(storyId);
      setShareUrl(url);
      setVisibility('public');
      if (mv) setMovement(mv);
      const r = await nativeShare({ title: title || t('shareTitle'), text: t('shareText'), url });
      if (r === 'copied') flash(t('linkCopied'));
      else if (r === 'shared') flash(t('shared'));
    } catch (e) { flash(e.message || t('saveError')); }
    setBusy(false);
  }

  async function finish() {
    if (busy) return;
    setBusy(true);
    try {
      await outreachApi.updateStory(storyId, { title, promptAnswers: answers, combinedHtml: html, coverImage, visibility, ...movementFields() });
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) { flash(e.message || t('saveError')); setBusy(false); }
  }

  async function handleDelete() {
    if (busy || !storyId) return;
    if (!window.confirm(t('deleteConfirm', title))) return;
    setBusy(true);
    try {
      await outreachApi.deleteStory(storyId);
      (onDeleted || onSaved) && (onDeleted || onSaved)();
      onClose && onClose();
    } catch (e) { flash(e.message || t('saveError')); setBusy(false); }
  }

  function closeOnBackdrop(e) {
    if (e.target === e.currentTarget && !busy) onClose && onClose();
  }

  const STEPS = [1, 2, 3, 4].map((n, i) => ({ num: n, label: stepLabels[i] }));

  const body = loading ? (
    <div className="sw-modal-loading"><span className="spinner" style={{ width: 30, height: 30 }} /></div>
  ) : loadFailed ? (
    <div className="empty-state"><h3>{t('loadError')}</h3>
      <button className="btn btn-primary" onClick={onClose}>{t('back')}</button></div>
  ) : (
    <>
      <div className="sw-progress">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`sw-step${step === s.num ? ' active' : step > s.num ? ' done' : ''}`}>
              <div className="sw-step-dot">{step > s.num ? <Check size={13} /> : s.num}</div>
              <span className="sw-step-label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`sw-step-line${step > s.num ? ' done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — pick a starting point */}
      {step === 1 && (
        <div className="sw-step-body">
          <h2 className="sw-h">{t('pickTemplateTitle')}</h2>
          <p className="sw-sub">{t('pickTemplateIntro')}</p>
          <div className="sw-templates">
            {STORY_TEMPLATES.map(tp => {
              const Icon = tp.Icon;
              return (
                <button
                  key={tp.id}
                  className={`sw-template${templateId === tp.id ? ' selected' : ''}`}
                  style={templateId === tp.id ? { borderColor: tp.color } : undefined}
                  onClick={() => setTemplateId(tp.id)}>
                  <span className="sw-template-icon" style={{ background: `${tp.color}1a`, color: tp.color }}><Icon size={20} /></span>
                  <span className="sw-template-text">
                    <span className="sw-template-label">{t(`templates.${tp.id}.label`)}</span>
                    <span className="sw-template-desc">{t(`templates.${tp.id}.desc`)}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <label className="sw-label">{t('titleLabel')}</label>
          <input className="sw-input" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('titlePlaceholder')} maxLength={120} />
          <div className="sw-nav">
            <span />
            <button className="btn btn-primary" disabled={!templateId || busy} onClick={startPrompts}>
              {t('next')} <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — one prompt at a time */}
      {step === 2 && tpl && (
        <div className="sw-step-body">
          <div className="sw-prompt-progress">
            <span>{t('questionOf', promptIdx + 1, prompts.length)}</span>
            <div className="sw-prompt-bar"><div style={{ width: `${((promptIdx + 1) / prompts.length) * 100}%`, background: tpl.color }} /></div>
          </div>
          <h2 className="sw-h">{t(`${promptCopyKey(tpl.id, prompts[promptIdx])}.label`)}</h2>
          <p className="sw-example">{t(`${promptCopyKey(tpl.id, prompts[promptIdx])}.example`)}</p>
          <textarea
            className="sw-textarea"
            value={answers[prompts[promptIdx]] || ''}
            onChange={e => setAnswers(a => ({ ...a, [prompts[promptIdx]]: e.target.value }))}
            placeholder={t(`${promptCopyKey(tpl.id, prompts[promptIdx])}.placeholder`)}
            rows={5}
            autoFocus
          />
          <p className="sw-hint">{t('promptStepHint')}</p>
          <div className="sw-nav">
            <button className="btn btn-ghost" onClick={() => promptIdx === 0 ? setStep(1) : setPromptIdx(i => i - 1)}>
              <ArrowLeft size={15} /> {t('back')}
            </button>
            <div className="sw-nav-right">
              {promptIdx < prompts.length - 1 ? (
                <>
                  <button className="btn btn-ghost" onClick={() => setPromptIdx(i => i + 1)}>{t('skip')}</button>
                  <button className="btn btn-primary" disabled={busy} onClick={() => setPromptIdx(i => i + 1)}>{t('next')} <ArrowRight size={15} /></button>
                </>
              ) : html ? (
                <>
                  <button className="btn btn-ghost" disabled={busy} onClick={() => finishPrompts(false)}>{t('keepStory')}</button>
                  <button className="btn btn-primary" disabled={busy} onClick={() => finishPrompts(true)}><Sparkles size={15} /> {t('regenerate')}</button>
                </>
              ) : (
                <button className="btn btn-primary" disabled={busy} onClick={() => finishPrompts(true)}><Sparkles size={15} /> {t('generate')}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3 — AI draft + edit */}
      {step === 3 && (
        <div className="sw-step-body">
          <h2 className="sw-h">{t('draftTitle')}</h2>
          <p className="sw-sub">{t('draftHint')}</p>

          <label className="sw-label">{t('titleLabel')}</label>
          <input className="sw-input" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('titlePlaceholder')} maxLength={120} />

          <button className="btn btn-outline sw-regen" disabled={generating} onClick={generate}>
            <Sparkles size={15} /> {generating ? t('generating') : (html ? t('regenerate') : t('generate'))}
          </button>
          <div className="sw-editor">
            <RichEditor value={html} onChange={setHtml} placeholder={t('editorPlaceholder')} minHeight={220} />
          </div>

          {/* Cover image (optional) — upload one or generate with AI */}
          <label className="sw-label">{t('coverLabel')}</label>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickCover} />
          {(coverPreview || coverImage) ? (
            <div className="sw-cover-wrap">
              <img src={coverPreview || coverImage} alt="" className="sw-cover-img" />
              <button type="button" className="sw-cover-remove" onClick={removeCover} disabled={coverBusy}><X size={13} /> {t('coverRemove')}</button>
            </div>
          ) : (
            <div className="sw-cover-actions">
              <button type="button" className="btn btn-outline" disabled={coverBusy} onClick={() => fileRef.current?.click()}>
                {coverBusy ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <ImagePlus size={15} />} {t('coverUpload')}
              </button>
              <button type="button" className="btn btn-outline" disabled={coverGenerating || !html} onClick={generateCover} title={!html ? t('coverNeedsDraft') : undefined}>
                {coverGenerating ? <><span className="spinner" style={{ width: 13, height: 13 }} /> {t('coverGenerating')}</> : <><Sparkles size={15} /> {t('coverGenerate')}</>}
              </button>
            </div>
          )}

          <div className="sw-nav">
            <button className="btn btn-ghost" onClick={() => setStep(2)}><ArrowLeft size={15} /> {t('editNotes')}</button>
            <button className="btn btn-primary" disabled={busy || generating || !html} onClick={draftNext}>{t('next')} <ArrowRight size={15} /></button>
          </div>
        </div>
      )}

      {/* Step 4 — visibility + share */}
      {step === 4 && (
        <div className="sw-step-body">
          <h2 className="sw-h">{t('visibilityTitle')}</h2>
          <div className="sw-vis-list">
            {VIS_OPTIONS.map(o => {
              const Icon = o.Icon;
              return (
                <button key={o.id} className={`sw-vis${visibility === o.id ? ' selected' : ''}`} onClick={() => setVisibility(o.id)}>
                  <span className="sw-vis-icon"><Icon size={17} /></span>
                  <span className="sw-vis-text">
                    <span className="sw-vis-label">{t(`visibilityOptions.${o.id}.label`)}</span>
                    <span className="sw-vis-desc">{t(`visibilityOptions.${o.id}.desc`)}</span>
                  </span>
                  {visibility === o.id && <Check size={16} className="sw-vis-check" />}
                </button>
              );
            })}
          </div>
          {visibility === 'public' && (
            <div className="sw-movement">
              <h3 className="sw-movement-h">{t('movementSection')}</h3>
              <p className="sw-movement-intro">{t('movementIntro')}</p>

              {movement && movement.moderationStatus === 'pending' && (
                <div className="sw-movement-status pending">{t('movementPending')}</div>
              )}
              {movement && movement.moderationStatus === 'approved' && movement.publishedAt && (
                <div className="sw-movement-status live">
                  {t('movementLive')}{' '}
                  <a href={`/m/${movement.id}`} target="_blank" rel="noreferrer">{t('movementViewInFeed')}</a>
                </div>
              )}

              <label className="sw-label">{t('locationLabel')}</label>
              <input className="sw-input" value={location} onChange={e => setLocation(e.target.value)}
                placeholder={t('locationPlaceholder')} maxLength={120} />

              <label className="sw-label">{t('categoryLabel')}</label>
              <select className="sw-input" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">{t('categoryNone')}</option>
                {MOVEMENT_CATEGORIES.map(c => <option key={c} value={c}>{t(`movementCategories.${c}`)}</option>)}
              </select>

              <label className="sw-checkbox">
                <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} />
                <span>{t('anonymousLabel')}</span>
              </label>
              {isAnonymous && (
                <input className="sw-input" value={submitterName} onChange={e => setSubmitterName(e.target.value)}
                  placeholder={t('anonymousNamePlaceholder')} maxLength={80} />
              )}

              <p className="sw-movement-consent">{t('publicConsent')}</p>

              <button className="btn btn-outline sw-share" disabled={busy} onClick={handleShare}>
                <Share2 size={15} /> {shareUrl ? t('shareAgain') : t('getLink')}
              </button>
              {shareUrl && <div className="sw-link">{shareUrl}</div>}
            </div>
          )}
          <div className="sw-nav">
            <button className="btn btn-ghost" onClick={() => setStep(3)}><ArrowLeft size={15} /> {t('back')}</button>
            <button className="btn btn-primary" disabled={busy} onClick={finish}><Check size={15} /> {t('finish')}</button>
          </div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={closeOnBackdrop}>
      <div className="modal sw-modal">
        <div className="modal-header">
          <span className="modal-title">{editMode ? t('editTitle') : t('newStory')}</span>
          <div className="sw-modal-head-actions">
            {editMode && !loading && (
              <button className="modal-close sw-modal-delete" onClick={handleDelete} title={t('deleteStory')} disabled={busy}><Trash2 size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        {body}
        {toast && <div className="sw-toast">{toast}</div>}
      </div>
    </div>,
    document.body
  );
}
